import { Auth0Client } from "@auth0/auth0-spa-js";
import axios from "axios";
import {
  CalculateSeatChangeImpactDto,
  SeatChangeImpactResponseDto,
} from "../../types/subscription";
import { apiConfig } from "../../config";

class SubscriptionClient {
  private auth0Client: Auth0Client;

  constructor(auth0Client: Auth0Client) {
    this.auth0Client = auth0Client;
  }

  private async getAuthHeaders() {
    const token = await this.auth0Client.getTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getSubscription(auth0OrgId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    try {
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}`,
        { headers },
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateSubscription(auth0OrgId: string, payload: any): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}`,
      payload,
      { headers },
    );
    return response.data;
  }

  async updateSubscriptionQuantity(
    auth0OrgId: string,
    quantity: number,
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.patch(
      `${apiConfig.providerServiceBase}/subscription/v1/api/subscriptions/${auth0OrgId}/quantity`,
      { quantity },
      { headers },
    );
    return response.data;
  }

  async updatePhysicians(
    auth0OrgId: string,
    change: "add" | "remove",
    count: number,
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/physicians`,
      { change, count },
      { headers },
    );
    return response.data;
  }

  async createProvisionalBillingLedgerEntry(
    auth0OrgId: string,
    payload: any,
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/billing-ledger/provisional`,
      payload,
      { headers },
    );
    return response.data;
  }

  async confirmProrationEvents(
    auth0OrgId: string,
    eventIds: string[],
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/proration/confirm`,
      { eventIds },
      { headers },
    );
    return response.data;
  }

  async cancelProrationEvents(
    auth0OrgId: string,
    eventIds: string[],
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/proration/cancel`,
      { eventIds },
      { headers },
    );
  }

  async rollbackProrationEvents(
    auth0OrgId: string,
    eventIds: string[],
  ): Promise<any> {
    const headers = await this.getAuthHeaders();
    await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/proration/rollback`,
      { eventIds },
      { headers },
    );
  }

  async getBillingLedgerEntries(
    auth0OrgId: string,
    options?: any,
  ): Promise<any[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();

    if (options?.type) {
      params.append("type", options.type);
    }
    if (options?.status) {
      if (Array.isArray(options.status)) {
        options.status.forEach((s: string) => params.append("status", s));
      } else {
        params.append("status", options.status);
      }
    }
    if (options?.limit !== undefined) {
      params.append("limit", options.limit.toString());
    }
    if (options?.offset !== undefined) {
      params.append("offset", options.offset.toString());
    }

    const queryString = params.toString();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/subscription/v1/api/internal/orgs/${auth0OrgId}/ledger${queryString ? `?${queryString}` : ""}`,
      { headers },
    );
    return response.data;
  }

  async getInvoices(auth0OrgId: string, options?: any): Promise<any[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();

    if (options?.status) {
      params.append("status", options.status);
    }
    if (options?.limit !== undefined) {
      params.append("limit", options.limit.toString());
    }
    if (options?.offset !== undefined) {
      params.append("offset", options.offset.toString());
    }

    const queryString = params.toString();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/org/${auth0OrgId}${queryString ? `?${queryString}` : ""}`,
      { headers },
    );
    return response.data;
  }

  async cancelSubscription(auth0OrgId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/cancel`,
      {},
      { headers },
    );
    return response.data;
  }

  async calculateSeatChangeImpact(
    auth0OrgId: string,
    payload: CalculateSeatChangeImpactDto,
  ): Promise<SeatChangeImpactResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/calculate-seat-change-impact`,
      payload,
      { headers },
    );
    return response.data;
  }

  async calculateAddonChangeImpact(
    auth0OrgId: string,
    payload: { operation: "add" | "remove"; addonKey: string },
  ): Promise<SeatChangeImpactResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/calculate-addon-change-impact`,
      payload,
      { headers },
    );
    return response.data;
  }

  async calculateTransitionImpact(
    auth0OrgId: string,
    payload: {
      targetTier: string;
      targetSeats: number;
      addons: string[];
      interval: string;
    },
  ): Promise<SeatChangeImpactResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/calculate-transition-impact`,
      payload,
      { headers },
    );
    return response.data;
  }

  async calculateInitialPeriodImpact(auth0OrgId: string): Promise<{
    willCharge: boolean;
    amountCents: number;
    currency: string;
    periodStart?: string;
    periodEnd?: string;
    description?: string;
    skipReason?: string;
  }> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/calculate-initial-period-impact`,
      { headers },
    );
    return response.data;
  }

  async changeTier(
    auth0OrgId: string,
    payload: { newTier: string; effectiveDate?: string; dryRun?: boolean },
  ): Promise<SeatChangeImpactResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/change-tier`,
      payload,
      { headers },
    );
    return response.data;
  }

  async requestAddonRemoval(
    auth0OrgId: string,
    addonKey: string,
    payload: { reason: string; userEmail: string; userName: string },
  ): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/subscriptions/org/${auth0OrgId}/addons/${addonKey}/remove-request`,
      payload,
      { headers },
    );
    return response.data;
  }
}

export default SubscriptionClient;
